import { c as _c } from "react/compiler-runtime";
import { faAngleLeft, faAngleRight } from '@fortawesome/pro-regular-svg-icons';
import { DateTime, WeekdayNumbers } from 'luxon';
import React from 'react';
import Button, { ButtonGroup } from '../button';
import Dropdown from '../dropdown';
import { Box, Flex, Grid } from '../grid';
import { CroppedText } from '../typography';
import { getCalendarPage } from '../utils/time';
const getMonths = (year, month, nowDt) => {
  const today = nowDt;
  let currentMonthIdx = 0;
  const calendarMonths = [];
  while (currentMonthIdx < 12) {
    if (currentMonthIdx % 3 === 0) {
      calendarMonths.push([]);
    }
    const date = DateTime.fromJSDate(new Date(year, month + currentMonthIdx, 1));
    const data = {
      month: date.month,
      showYear: false,
      date: date.toISO().substring(0, 19),
      current: date.month === today.month
    };
    calendarMonths[Math.floor(currentMonthIdx / 3)].push(data);
    currentMonthIdx++;
  }
  return calendarMonths;
};
type CalendarPickerProps = {
  mode: string;
  value: string;
  onChange: (string) => void;
  disablePast?: boolean;
  className?: string;
  language: string;
  timezone: string;
  variant?: 'solid' | 'flat' | 'outlined';
  color?: string;
};
const CalendarPicker = t0 => {
  const $ = _c(39);
  const {
    mode,
    value: inputDate,
    onChange: onChange_,
    language,
    timezone,
    variant: t1,
    color: t2
  } = t0;
  const variant = t1 === undefined ? "solid" : t1;
  const color = t2 === undefined ? "secondary" : t2;
  let t3;
  if ($[0] !== timezone) {
    t3 = DateTime.utc().setZone(timezone).setZone("utc", {
      keepLocalTime: true
    });
    $[0] = timezone;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const nowDt = t3;
  const [isOpen, setIsOpen] = React.useState(false);
  const [dropdownDate, setDropdownDate] = React.useState(inputDate);
  if (inputDate?.endsWith("Z")) {
    console.error("Input date needs to be local");
  }
  const inputDateTime = DateTime.fromISO(inputDate);
  let startDateTime = inputDateTime.startOf("day");
  let endDateTime = inputDateTime.endOf("day");
  const showWeek = mode === "week" || mode === "agenda";
  if (showWeek) {
    startDateTime = startDateTime.weekday === 0 as WeekdayNumbers ? startDateTime.minus({
      day: 1
    }).set({
      weekday: 1
    }) : startDateTime.set({
      weekday: 1
    });
    endDateTime = startDateTime.plus({
      day: 6
    }).endOf("day");
  }
  let t4;
  let t5;
  if ($[2] !== inputDate) {
    t4 = () => {
      setDropdownDate(inputDate);
    };
    t5 = [inputDate];
    $[2] = inputDate;
    $[3] = t4;
    $[4] = t5;
  } else {
    t4 = $[3];
    t5 = $[4];
  }
  React.useEffect(t4, t5);
  const isSelectedDay = date_ => {
    const date = DateTime.fromISO(date_);
    if (startDateTime <= date && date <= endDateTime) {
      return true;
    }
    return false;
  };
  let t6;
  if ($[5] !== inputDate) {
    t6 = val => {
      setIsOpen(val);
      setDropdownDate(inputDate);
    };
    $[5] = inputDate;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const setOpen = t6;
  let t7;
  if ($[7] !== setOpen) {
    t7 = () => setOpen(true);
    $[7] = setOpen;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  const open = t7;
  let t8;
  if ($[9] !== setOpen) {
    t8 = () => setOpen(false);
    $[9] = setOpen;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  const close = t8;
  let t9;
  if ($[11] !== close || $[12] !== onChange_) {
    t9 = v => {
      onChange_(v);
      close();
    };
    $[11] = close;
    $[12] = onChange_;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  const onChange = t9;
  const changeDate = pos => {
    if (showWeek) {
      onChange(inputDateTime.plus({
        weeks: pos
      }).toISO().substring(0, 19));
    } else {
      if (mode === "month") {
        onChange(inputDateTime.plus({
          months: pos
        }).toISO().substring(0, 19));
      } else {
        onChange(inputDateTime.plus({
          days: pos
        }).toISO().substring(0, 19));
      }
    }
  };
  let t10;
  if ($[14] !== dropdownDate || $[15] !== mode) {
    t10 = pos_0 => {
      if (mode === "month") {
        setDropdownDate(DateTime.fromISO(dropdownDate).plus({
          years: pos_0
        }).toISO().substring(0, 19));
      } else {
        setDropdownDate(DateTime.fromISO(dropdownDate).plus({
          month: pos_0
        }).toISO().substring(0, 19));
      }
    };
    $[14] = dropdownDate;
    $[15] = mode;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  const changeMiniDate = t10;
  let t11;
  if ($[17] !== mode || $[18] !== nowDt.year || $[19] !== showWeek) {
    t11 = (dt, t12) => {
      const small = t12 === undefined ? true : t12;
      const days = showWeek ? 7 : 1;
      const dateStart = DateTime.fromISO(dt);
      const dateEnd = dateStart.plus({
        days: days - 1
      });
      let year = "";
      const crossYear = dateEnd.year !== nowDt.year;
      if (mode === "month") {
        if (crossYear) {
          year = small ? "yy" : ", y";
        }
        return dateStart.toFormat((small ? "MMM" : "MMMM") + year);
      }
      if (crossYear && !small) {
        year = ", y";
      }
      if (!small && dateEnd.month !== dateStart.month) {
        return dateStart.toFormat("d MMM") + dateEnd.toFormat(` - d MMM${year}`);
      }
      if (dateEnd.day !== dateStart.day) {
        return dateStart.toFormat("d ") + dateEnd.toFormat((small ? " - d MMM" : " - d MMMM") + year);
      }
      return dateStart.toFormat((small ? "EEE d MMM" : "EEEE d MMMM") + year);
    };
    $[17] = mode;
    $[18] = nowDt.year;
    $[19] = showWeek;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  const label = t11;
  let t12;
  if ($[21] !== dropdownDate || $[22] !== nowDt) {
    t12 = getMonths(DateTime.fromISO(dropdownDate).year, 0, nowDt);
    $[21] = dropdownDate;
    $[22] = nowDt;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  const calendarMonths = t12;
  let t13;
  if ($[24] !== dropdownDate || $[25] !== timezone) {
    t13 = getCalendarPage(dropdownDate, timezone);
    $[24] = dropdownDate;
    $[25] = timezone;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  const calendarWeeks = t13.weeks;
  const T0 = Dropdown;
  const t14 = false;
  const t15 = props => <ButtonGroup data-sentry-element="ButtonGroup" data-sentry-component="t15" data-sentry-source-file="index.tsx"><Button variant={variant} color={color} onClick={() => changeDate(-1)} className="side" iconLeft={faAngleLeft} data-sentry-element="Button" data-sentry-source-file="index.tsx" /><Button px={4} variant={variant} color={color} className="middle" {...props} data-sentry-element="Button" data-sentry-source-file="index.tsx"><CroppedText className="hide-for-medium" sx={{
        whiteSpace: "nowrap"
      }} data-sentry-element="CroppedText" data-sentry-source-file="index.tsx">{label(inputDate, true)}</CroppedText><CroppedText className="show-for-medium" data-sentry-element="CroppedText" data-sentry-source-file="index.tsx">{label(inputDate, false)}</CroppedText></Button><Button variant={variant} color={color} onClick={() => changeDate(1)} className="side" iconLeft={faAngleRight} data-sentry-element="Button" data-sentry-source-file="index.tsx" /></ButtonGroup>;
  const T1 = Box;
  const t16 = 2;
  let t17;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = {
      border: "1px solid rgba(27, 31, 35, 0.15)",
      boxShadow: "medium",
      backgroundColor: "background",
      textAlign: "center",
      borderRadius: 2,
      maxWidth: "250px",
      zIndex: 1
    };
    $[27] = t17;
  } else {
    t17 = $[27];
  }
  const t18 = mode === "month" ? <><ButtonGroup><Button variant={variant} color={color} onClick={() => changeMiniDate(-1)} className="side" iconLeft={faAngleLeft} /><Button width="80%">{DateTime.fromISO(dropdownDate).toFormat("y")}</Button><Button variant={variant} color={color} onClick={() => changeMiniDate(1)} className="side" iconLeft={faAngleRight} /></ButtonGroup><Box pt={2} sx={{
      position: "relative",
      height: "100%"
    }}><Grid gridTemplateColumns="repeat(3, 1fr)" gridGap={0}>{calendarMonths.map((row, idx) => <React.Fragment key={idx}>{row.map((t19, idx_0) => {
            const {
              date: date_0,
              current
            } = t19;
            return <Flex key={idx_0} width="32px" height="32px" justifyContent="center" alignItems="center" role="button" tabIndex={0} onClick={() => onChange(date_0)} onKeyPress={e => e.key === "Enter" && onChange(date_0)} sx={{
              cursor: "pointer",
              color: "text",
              ...(inputDateTime.hasSame(DateTime.fromISO(date_0), "month") ? {
                backgroundColor: "primary",
                color: "white"
              } : {}),
              ...(current ? {
                backgroundColor: "background",
                fontWeight: "heading",
                "a:first-of-type": {
                  borderWidth: "2px",
                  borderColor: "primary",
                  borderStyle: "solid"
                },
                color: "primary"
              } : {})
            }}><CroppedText>{DateTime.fromISO(date_0).toFormat("MMM")}</CroppedText></Flex>;
          })}</React.Fragment>)}</Grid></Box></> : <><ButtonGroup><Button variant={variant} color={color} onClick={() => changeMiniDate(-1)} iconLeft={faAngleLeft} /><Button variant={variant} color={color} width="80%">{DateTime.fromISO(DateTime.fromISO(dropdownDate).hasSame(nowDt, "year") ? dropdownDate : dropdownDate).toFormat("MMMM")}</Button><Button variant={variant} color={color} onClick={() => changeMiniDate(1)} iconLeft={faAngleRight} /></ButtonGroup><Box pt={2} sx={{
      position: "relative",
      height: "100%"
    }}><Grid gridTemplateColumns="repeat(7, 1fr)" gridGap={0}>{[1, 2, 3, 4, 5, 6, 7].map(weekday => <Flex key={weekday} width="32px" height="32px" justifyContent="center" alignItems="center"><CroppedText>{nowDt.setLocale(language).set({
              weekday
            }).toFormat("ccc")}</CroppedText></Flex>)}{calendarWeeks.map((week, idx_1) => <React.Fragment key={idx_1}>{week.map((t20, idx2) => {
            const {
              date: date_1,
              inPast,
              current: current_0,
              day,
              currentMonth
            } = t20;
            return <Flex key={idx2} width="32px" height="32px" justifyContent="center" alignItems="center" role="button" tabIndex={0} onClick={() => onChange(date_1)} onKeyPress={e_0 => e_0.key === "Enter" && onChange(date_1)} sx={{
              cursor: "pointer",
              ...(!currentMonth && !isSelectedDay(date_1) ? {
                color: "gray.6"
              } : {}),
              ...(inPast ? {
                color: "gray.6",
                textDecoration: "line-through"
              } : {}),
              ...(isSelectedDay(date_1) ? {
                backgroundColor: "primary",
                color: "white"
              } : {}),
              ...(current_0 ? {
                backgroundColor: "background",
                fontWeight: "heading",
                borderColor: "primary",
                borderWidth: "1px",
                borderStyle: "solid",
                color: "primary"
              } : {})
            }}><CroppedText>{day}</CroppedText></Flex>;
          })}</React.Fragment>)}</Grid></Box></>;
  let t21;
  if ($[28] !== T1 || $[29] !== t17 || $[30] !== t18) {
    t21 = <T1 p={t16} sx={t17}>{t18}</T1>;
    $[28] = T1;
    $[29] = t17;
    $[30] = t18;
    $[31] = t21;
  } else {
    t21 = $[31];
  }
  let t22;
  if ($[32] !== T0 || $[33] !== close || $[34] !== isOpen || $[35] !== open || $[36] !== t15 || $[37] !== t21) {
    t22 = <T0 isOpen={isOpen} close={close} open={open} autoClose={t14} toggle={t15}>{t21}</T0>;
    $[32] = T0;
    $[33] = close;
    $[34] = isOpen;
    $[35] = open;
    $[36] = t15;
    $[37] = t21;
    $[38] = t22;
  } else {
    t22 = $[38];
  }
  return t22;
};
export default CalendarPicker;